import { $, _, WordCloud, SVG } from "js/vendor";
import React from "reactn";
import * as geom from "js/core/utilities/geom";
import { app } from "js/namespaces";
import { BaseElement } from "js/elements/base/BaseElement";
import { ForeColorType, BackgroundStyleType, AnimationSequenceType, TextStyleEnum } from "common/constants.js";
import { getCSSTransform } from "js/core/utilities/geom";
import { SVGGroup } from "js/core/utilities/svgHelpers";
import Spinner from "js/react/components/Spinner";

class WordCloudElement extends BaseElement {
    get font() {
        return this.model.font || "block";
    }

    get scale() {
        return this.model.scale || 25;
    }

    get style() {
        return this.model.style || "straight";
    }

    get spacing() {
        return (this.model.spacing !== undefined) ? this.model.spacing : 1;
    }

    get wordList() {
        return JSON.parse(this.model.wordcloud);
    }

    set wordList(value) {
        this.model.wordcloud = JSON.stringify(value);
    }

    getFontName(weight) {
        let fontWeight;
        switch (weight) {
            case "5":
                fontWeight = 700;
                break;
            default:
                fontWeight = 400;
        }

        if (this.model.font == "theme") {
            return app.fontManager.fontCache[app.currentTheme.styleSheet.$headerFontRegular].name;
        } else {
            if (fontWeight < 400) {
                return "BebasNeue Book";
            } else if (fontWeight > 400) {
                return "BebasNeue Bold";
            } else {
                return "BebasNeue Regular";
            }
        }
    }

    refreshElement(transition) {
        this.canvas.refreshElement(this, transition);
    }

    get canRefreshElement() {
        return true;
    }

    _build() {
        this.needsLayoutMigration = this.model.version == null || this.model.version < 2;
    }

    _load() {
        return Promise.all([
            app.fontManager.loadUnicodeFont("BebasNeue Book", "a", TextStyleEnum.REGULAR),
            app.fontManager.loadUnicodeFont("BebasNeue Regular", "a", TextStyleEnum.REGULAR),
            app.fontManager.loadUnicodeFont("BebasNeue Bold", "a", TextStyleEnum.BOLD)
        ]);
    }

    regenWordCloud(size, undo) {
        this.canvas.layouter.runPostRender(() =>
            this.generateWordCloud(size)
                .then(() => this.refreshElement(false))
                .then(() => this.canvas.saveCanvasModel({ undo }))
        );
    }

    _calcProps(props, options) {
        const { size } = props;
        const { isTryingLayout } = options;

        // Asked to regen by the editor
        if (this.shouldRegen) {
            this.shouldRegen = false;
            this.regenWordCloud(size, true);
            return { size, isLoading: true };
        }

        // Migration, we don't have to save the model immediately, it will be saved
        // upon next word cloud recalc, until then we can just assign this value
        if (!this.model.generatedForSize) {
            this.model.generatedForSize = size.toArray();
        }

        // Nothing has changed or trying layout
        if (_.isEqual(this.model.generatedForSize, size.toArray()) || isTryingLayout) {
            return { size, isLoading: false };
        }

        // Something has changed (i.e. layout)
        this.regenWordCloud(size, false);
        return { size, isLoading: true };
    }

    renderChildren(props, transition) {
        const { isLoading, bounds, styles } = props;

        if (isLoading) {
            return <Spinner />;
        }

        this.renderedWords = [];
        const children = [];
        for (const word of this.wordList) {
            let fontName;
            if (this.model.font == "theme" && app.currentTheme.styleSheet.$headerFontRegular[0] === "-") {
                // assumes fontName starting with - is a firebase id, revisit this
                fontName = app.currentTheme.styleSheet.$headerFontRegular;
            } else {
                fontName = this.getFontName(word.weight);
            }
            const font = app.fontManager.loadUnicodeFont(fontName, "a", TextStyleEnum.REGULAR);

            const path = font.getPath(word.word, 0, 0, word.layout.fontSize);

            word.textXOffset = (path.getBoundingBox().x2 - path.getBoundingBox().x1) * -0.5;
            word.textYOffset = path.getBoundingBox().y1 * -0.5;

            if (word.layout.rotation) {
                const rotation = word.layout.rotation;

                const bbox = path.getBoundingBox();
                const center = new geom.Point((bbox.x2 - bbox.x1) / 2 + bbox.x1, (bbox.y2 - bbox.y1) / 2 + bbox.y1);

                path.commands.forEach(command => {
                    if (command.x != undefined) {
                        const point = new geom.Point(command.x, command.y).rotate(rotation, center);
                        command.x = point.x;
                        command.y = point.y;
                    }
                    if (command.x1 != undefined) {
                        const point = new geom.Point(command.x1, command.y1).rotate(rotation, center);
                        command.x1 = point.x;
                        command.y1 = point.y;
                    }
                    if (command.x2 != undefined) {
                        const point = new geom.Point(command.x2, command.y2).rotate(rotation, center);
                        command.x2 = point.x;
                        command.y2 = point.y;
                    }
                });
            }

            let matrix = new SVG.Matrix();
            if (!this.needsLayoutMigration) {
                matrix = matrix.translate(-bounds.width / 2, -bounds.height / 2);
            }
            matrix = matrix.translate(word.layout.x, word.layout.y);
            matrix = matrix.translate(word.textXOffset, word.textYOffset);

            const values = matrix.extract();
            const pathStyles = {
                transform: getCSSTransform({
                    translateX: values.x,
                    translateY: values.y
                }),
                fill: this.getWordColor(styles, parseInt(word.layout.weight)),
                transition: transition ? "300ms" : "none"
            };

            word.ref = React.createRef();

            this.renderedWords.push(word);

            children.push(<path key={children.length} ref={word.ref} d={path.toPathData()} style={pathStyles} />);
        }

        return <SVGGroup key={this.id}>{children}</SVGGroup>;
    }

    getWordColor(styles, weight) {
        let bgColor = this.getBackgroundColor();

        if (this.canvas.getSlideColor() != ForeColorType.COLORFUL) {
            let baseColor = this.canvas.getTheme().palette.getForeColor(styles.color, this.getSlideColor(), this.getBackgroundColor());

            if (this.model.invertColors) {
                weight = 6 - weight;
            }
            let color;

            switch (this.canvas.getTheme().palette.getBackgroundColorType(bgColor)) {
                case BackgroundStyleType.DARK:
                    color = baseColor.lighten(1.5 * weight * weight);
                    break;
                case BackgroundStyleType.LIGHT:
                    color = baseColor.darken(1 * weight * weight);
                    break;
                case BackgroundStyleType.COLOR:
                    color = baseColor.lighten(1.5 * weight * weight);
                    break;
                default:
                    color = baseColor;
            }

            return color.toHexString();
        } else {
            let colorNames = this.canvas.getTheme().palette.getColorfulColors();

            if (this.model.invertColors) {
                weight = 6 - weight;
            }
            return this.canvas.getTheme().palette.getColor(colorNames[(weight - 1) % colorNames.length]).toHexString();
        }
    }

    async generateWordCloud(size) {
        if (this.generateWordCloudPromise) {
            await this.generateWordCloudPromise;
        }

        this.generateWordCloudPromise = this._generateWordCloud(size)
            .finally(() => this.generateWordCloudPromise = null);

        return this.generateWordCloudPromise;
    }

    _generateWordCloud(size) {
        // create an off-DOM canvas to render wordcloud to
        let $wordCloud = $.div("wordcloud");
        let $canvas = $wordCloud.addEl($(`<canvas width='${size.width * 2}' height='${size.height * 2}'></canvas>`));

        let hasShape = false;

        // setup word list
        let wordList;
        switch (this.model.sort || "size") {
            case "size":
                wordList = _.reverse(_.sortBy(this.wordList, o => o.weight));
                break;
            case "random":
                wordList = _.shuffle(this.wordList);
                break;
        }

        // strip any quotes
        for (let word of wordList) {
            if (word.word.startsWith('"') && word.word.endsWith('"')) {
                word.word = word.word.substr(1, word.word.length - 2);
            }
        }

        // create options for word cloud generator
        let options = {
            list: wordList,
            weightFactor: this.scale,
            gridSize: this.styles.gridSize || 0,
            shuffle: true,
            drawOutOfBound: true,
            shape: "circle",
            clearCanvas: hasShape ? false : true,
            spacing: this.spacing,
            fontCallback: weight => {
                const font = this.getFontName(weight);
                return `${weight * this.scale}px "${font}"`;
            }
        };

        switch (this.style) {
            case "straight":
                options.rotateRatio = 0;
                break;
            case "right_angles":
                options.rotateRatio = 0.33;
                options.rotationSteps = 2;
                break;
            case "rotated":
                options.rotateRatio = 0.60;
                options.rotationSteps = 6;
                break;
        }

        // run the word cloud gen
        WordCloud($canvas[0], options);

        return new Promise(resolve => {
            // wait for word cloud gen to finish
            $canvas.on("wordcloudstop", event => {
                $wordCloud = null; // throw away the offscreen canvas element

                // build our new model from the properties set by the word cloud generator on the wordlist
                this.wordList = wordList.map(({ word, weight, layout, drawn }) => ({
                    word,
                    weight,
                    layout,
                    drawn
                }));
                this.model.generatedForSize = size.toArray();
                this.model.version = 2;
                this.needsLayoutMigration = false;

                resolve();
            });
        });
    }

    registerAnimations(animationManager) {
        animationManager.findOrCreateAnimationSequence(this.id, "WordCloud").addAnimation({
            type: AnimationSequenceType.AUTOPLAY,
            prepare: () => {
                for (const word of this.renderedWords) {
                    $(word.ref.current).opacity(0);
                }
            },
            reset: () => {
                for (const word of this.renderedWords) {
                    $(word.ref.current).opacity(1);
                }
            },
            play: duration => {
                return Promise.all(this.renderedWords.map(word => new Promise(resolve => {
                    let matrix = new SVG.Matrix();

                    if (!this.needsLayoutMigration) {
                        matrix = matrix.translate(-this.bounds.width / 2, -this.bounds.height / 2);
                    }

                    matrix = matrix.translate(word.layout.x, word.layout.y);
                    matrix = matrix.translate(word.textXOffset, word.textYOffset);

                    const matrixValues = matrix.extract();

                    const animation = {
                        translateX: [matrixValues.x, matrixValues.x],
                        translateY: [matrixValues.y, matrixValues.y],
                        opacity: [1, 0]
                    };

                    if (matrixValues.x < this.innerBounds.width / 2) {
                        const m = matrix.translate(-matrixValues.x - 300, 0);
                        animation.translateX[1] = m.extract().x;
                        animation.translateY[1] = m.extract().y;
                    } else {
                        const m = matrix.translate(matrixValues.x + 300, 0);
                        animation.translateX[1] = m.extract().x;
                        animation.translateY[1] = m.extract().y;
                    }

                    $(word.ref.current).css({ transition: "none" });

                    $(word.ref.current).velocity(animation, {
                        duration: Math.random() * duration * 3 + 1000,
                        delay: Math.random() * 1000,
                        complete: () => {
                            $(word.ref.current).css({ transition: "300ms" });
                            resolve();
                        }
                    });
                })));
            }
        });
    }
}

export { WordCloudElement };

export const elements = {
    WordCloudElement,
};
